import { useCallback, useEffect, useRef, useState } from 'react'
import type { ImageFile } from '~/features/shared/components/images-section'
import {
  clearEventDraft,
  loadDraftImages,
  readEventDraft,
  saveDraftImages,
  writeEventDraft,
} from './event-draft'

const SAVE_DELAY_MS = 400

// biome-ignore lint/suspicious/noExplicitAny: TanStack Form API has too many generic params to type here
type AnyForm = any

export function useEventDraft({
  businessId,
  form,
  enabled,
  skipRestore,
  images,
  listingId,
  onRestore,
  onRestoreImages,
}: {
  businessId: string
  form: AnyForm
  enabled: boolean
  skipRestore: boolean
  images: ImageFile[]
  listingId: string | null | undefined
  onRestore: (draft: {
    values: Record<string, unknown>
    listingId: string | null | undefined
  }) => void
  onRestoreImages: (images: ImageFile[]) => void
}) {
  const [restored, setRestored] = useState(false)
  const finished = useRef(false)
  const checkedFor = useRef<string | null>(null)
  const imagesReady = useRef(false)
  const timer = useRef<number | undefined>(undefined)
  const latest = useRef({ form, businessId, enabled, listingId })
  latest.current = { form, businessId, enabled, listingId }
  const callbacks = useRef({ onRestore, onRestoreImages })
  callbacks.current = { onRestore, onRestoreImages }

  const save = useCallback(() => {
    window.clearTimeout(timer.current)
    const current = latest.current
    if (!current.enabled || finished.current || !current.businessId) return
    if (!current.form.state.isDirty) return
    writeEventDraft(current.businessId, {
      values: current.form.state.values,
      listingId: current.listingId,
    })
  }, [])

  useEffect(() => {
    if (!enabled || !businessId || checkedFor.current === businessId) return
    checkedFor.current = businessId
    const draft = skipRestore ? null : readEventDraft<Record<string, unknown>>(businessId)
    if (!draft) {
      imagesReady.current = true
      return
    }
    for (const [key, value] of Object.entries(draft.values)) form.setFieldValue(key, value)
    callbacks.current.onRestore({ values: draft.values, listingId: draft.listingId })
    setRestored(true)
    loadDraftImages(businessId).then((stored) => {
      imagesReady.current = true
      if (stored.length > 0 && !finished.current) callbacks.current.onRestoreImages(stored)
    })
  }, [enabled, businessId, skipRestore, form])

  useEffect(() => {
    if (!enabled) return
    const subscription = form.store.subscribe(() => {
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(save, SAVE_DELAY_MS)
    })
    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') save()
    }
    window.addEventListener('pagehide', save)
    document.addEventListener('visibilitychange', flushWhenHidden)
    return () => {
      subscription.unsubscribe()
      window.removeEventListener('pagehide', save)
      document.removeEventListener('visibilitychange', flushWhenHidden)
      save()
    }
  }, [enabled, form, save])

  useEffect(() => {
    if (!enabled || !businessId || !imagesReady.current || finished.current) return
    saveDraftImages(businessId, images)
  }, [enabled, businessId, images])

  useEffect(() => {
    if (listingId !== undefined) save()
  }, [listingId, save])

  return {
    restored,
    hideRestoredNote: () => setRestored(false),
    clear: () => {
      finished.current = true
      window.clearTimeout(timer.current)
      clearEventDraft(latest.current.businessId)
      setRestored(false)
    },
  }
}
